import type { RunnitNodeRunResult } from '@run-diffusion/shared';

export type TemplateDialogMode = 'create' | 'edit';

export interface AvatarLike {
	id: string;
	[key: string]: unknown;
}

export interface RunnitLike {
	id?: string;
	title?: string | null;
	description?: string | null;
	onboardingData?: Record<string, unknown> | null;
	customFields?: Record<string, unknown> | null;
	avatarId?: string | null;
	avatar?: AvatarLike | null;
}

export interface TemplateNodeLike {
	id: string;
	nodeDefId?: string | null;
	title?: string | null;
	description?: string | null;
}

export interface SelectedResultLike {
	templateNodeId: string;
	sourceNodeRunId: string;
	resultUuid: string;
	result?: RunnitNodeRunResult;
}

export interface TemplateDialogInitSnapshot {
	templateTitle: string | null;
	templateDescription: string | null;
	templateOnboardingData: Record<string, unknown>;
	templateCustomFields: Record<string, unknown>;
	templateAvatarId: string | null;
	templateAvatar: AvatarLike | null;
	currentRunnitNodes: TemplateNodeLike[];
	selectedResults: SelectedResultLike[];
	currentStep: number;
}

export interface BuildTemplatePayloadInput {
	mode: TemplateDialogMode;
	templateId?: string;
	teamId: string | null;
	templateTitle: string | null;
	templateDescription: string | null;
	templateAvatarId: string | null;
	templateOnboardingData: Record<string, unknown>;
	customFields: Record<string, unknown>;
	nodes: TemplateNodeLike[];
	selectedResults: SelectedResultLike[];
}

export interface BuildTemplatePayloadOutput {
	templateId?: string;
	template: {
		title: string | null;
		description: string | null;
		avatarId: string | null;
		onboardingData: Record<string, unknown>;
		customFields: Record<string, unknown>;
		teamId?: string | null;
	};
	nodes: Array<{
		id: string;
		nodeDefId?: string | null;
		title?: string | null;
		description?: string | null;
	}>;
	selectedResults: Array<{
		templateNodeId: string;
		sourceNodeRunId: string;
		resultUuid: string;
	}>;
}

export interface ShouldPrefillSelectedResultsInput {
	duplicateTemplate: boolean;
	runnitType: string | null | undefined;
}

export interface CustomFieldDefLike {
	key: string;
	type: string;
}

export interface ExtractTeamStringCustomFieldsInput {
	ownerSelection: string | null | undefined;
	fieldDefs: CustomFieldDefLike[];
	templateCustomFields: Record<string, unknown>;
}

export async function initializeNoSourceCreateSnapshot (getRandomDefaultAvatar: () => Promise<AvatarLike | null>): Promise<TemplateDialogInitSnapshot> {
	const chosenRandomAvatar = await getRandomDefaultAvatar();

	return {
		templateTitle: null,
		templateDescription: null,
		templateOnboardingData: {},
		templateCustomFields: {},
		templateAvatarId: chosenRandomAvatar?.id ?? null,
		templateAvatar: chosenRandomAvatar ?? null,
		currentRunnitNodes: [],
		selectedResults: [],
		currentStep: 1,
	};
}

export function initializeEditSnapshot (runnit: RunnitLike): Omit<TemplateDialogInitSnapshot, 'currentRunnitNodes' | 'selectedResults'> {
	return {
		templateTitle: runnit.title ?? null,
		templateDescription: runnit.description ?? null,
		templateOnboardingData: { ...(runnit.onboardingData ?? {}) },
		templateCustomFields: { ...(runnit.customFields ?? {}) },
		templateAvatarId: runnit.avatarId ?? null,
		templateAvatar: runnit.avatar ?? null,
		currentStep: 1,
	};
}

export async function initializeEditSnapshotWithAvatarFallback (
	runnit: RunnitLike,
	getRandomDefaultAvatar: () => Promise<AvatarLike | null>,
): Promise<Omit<TemplateDialogInitSnapshot, 'currentRunnitNodes' | 'selectedResults'>> {
	const base = initializeEditSnapshot(runnit);

	if (base.templateAvatarId && base.templateAvatar) {
		return base;
	}

	const chosenRandomAvatar = await getRandomDefaultAvatar();

	return {
		...base,
		templateAvatarId: chosenRandomAvatar?.id ?? null,
		templateAvatar: chosenRandomAvatar ?? null,
	};
}

export function buildTemplatePayload (input: BuildTemplatePayloadInput): BuildTemplatePayloadOutput {
	const base = {
		template: {
			title: input.templateTitle,
			description: input.templateDescription,
			avatarId: input.templateAvatarId,
			onboardingData: input.templateOnboardingData,
			customFields: input.customFields,
		},
		nodes: input.nodes.map((node) => ({
			id: node.id,
			nodeDefId: node.nodeDefId,
			title: node.title,
			description: node.description,
		})),
		selectedResults: input.selectedResults.map((result) => ({
			templateNodeId: result.templateNodeId,
			sourceNodeRunId: result.sourceNodeRunId,
			resultUuid: result.resultUuid,
		})),
	};

	if (input.mode === 'create') {
		return {
			...base,
			template: {
				...base.template,
				teamId: input.teamId,
			},
		};
	}

	return {
		...base,
		templateId: input.templateId,
	};
}

export function shouldPrefillSelectedResults (input: ShouldPrefillSelectedResultsInput): boolean {
	return input.duplicateTemplate && input.runnitType === 'TEMPLATE';
}

/** Edit template always hydrates step-3 selections from Firestore; clone-from-source only when duplicating a template board. */
export function shouldLoadExistingTemplateNodeRuns (input: {
	isEditMode: boolean;
} & ShouldPrefillSelectedResultsInput): boolean {
	if (input.isEditMode) {
		return true;
	}
	return shouldPrefillSelectedResults(input);
}

export function extractTeamStringCustomFields (input: ExtractTeamStringCustomFieldsInput): Record<string, string> {
	if (input.ownerSelection !== 'TEAM') {
		return {};
	}

	const output: Record<string, string> = {};
	for (const def of input.fieldDefs) {
		if (def.type !== 'STR') continue;
		const value = String(input.templateCustomFields[def.key] ?? '').trim();
		if (value) {
			output[def.key] = value;
		}
	}

	return output;
}
